import { useState, useMemo, useCallback } from 'react';

interface PaginationResult<T> {
  currentPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  paginatedItems: T[];
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  goToPage: (page: number) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  resetPage: () => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  hasPrevious: boolean;
  hasNext: boolean;
  isFirstPage: boolean;
  isLastPage: boolean;
  showingFrom: number;
  showingTo: number;
  totalItems: number;
}

/**
 * A reusable pagination hook
 */
export default function usePagination<T = unknown>(
  items: T[] = [],
  itemsPerPage: number = 10,
  initialPage: number = 1
): PaginationResult<T> {
  const [currentPage, setCurrentPage] = useState<number>(initialPage);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(items.length / itemsPerPage);
  }, [items.length, itemsPerPage]);

  // Calculate start and end indices
  const startIndex = useMemo(() => {
    return (currentPage - 1) * itemsPerPage;
  }, [currentPage, itemsPerPage]);

  const endIndex = useMemo(() => {
    return startIndex + itemsPerPage;
  }, [startIndex, itemsPerPage]);

  // Get current page items
  const paginatedItems = useMemo(() => {
    return items.slice(startIndex, endIndex);
  }, [items, startIndex, endIndex]);

  // Navigation handlers
  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  }, [totalPages]);

  const goToPage = useCallback(
    (page: number) => {
      const pageNumber = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(pageNumber);
    },
    [totalPages]
  );

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const goToLastPage = useCallback(() => {
    setCurrentPage(totalPages);
  }, [totalPages]);

  // Reset to first page (useful when items change)
  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Pagination info
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  // Display info
  const showingFrom = items.length > 0 ? startIndex + 1 : 0;
  const showingTo = Math.min(endIndex, items.length);
  const totalItems = items.length;

  return {
    // Current state
    currentPage,
    totalPages,
    startIndex,
    endIndex,

    // Paginated data
    paginatedItems,

    // Navigation handlers
    handlePreviousPage,
    handleNextPage,
    goToPage,
    goToFirstPage,
    goToLastPage,
    resetPage,
    setCurrentPage,

    // Boolean flags
    hasPrevious,
    hasNext,
    isFirstPage,
    isLastPage,

    // Display info
    showingFrom,
    showingTo,
    totalItems,
  };
}
